import { ApiRequestError, Badge, EmptyState, Skeleton, Textarea, useErrorToast } from '@h2hspark/ui';
import { CalendarCheck, Check, Copy, ExternalLink, PenLine, Send } from 'lucide-react';
import * as React from 'react';
import { useParams } from 'react-router-dom';

import { initialsOf } from '../connections/constants';
import { PER_EVENT_LOCKED } from '../event-registrations/constants';
import { BackToYourEvents, PerEventShell } from '../event-registrations/per-event-page';

import {
  useCopyFollowUp,
  useFollowUpForEvent,
  useMarkFollowUpDone,
  useSaveFollowUpDraft,
  useSendFollowUp,
} from './api';
import {
  CHANNELS_LABEL,
  CONNECTIONS_COUNT_LABEL,
  COPIED_LABEL,
  COPIED_NOTICE,
  COPY_FAILED,
  COPY_OPENER_LABEL,
  SEND_OPENER_DISCONNECTED,
  SEND_OPENER_FAILED,
  SEND_OPENER_LABEL,
  SEND_OPENER_RATE_LIMITED,
  sendOpenerAccessibleName,
  DRAFT_LABEL,
  FOLLOW_UP_COUNT_LABEL,
  MARK_DONE_DESCRIPTION,
  MARK_DONE_LABEL,
  NO_CHANNEL_NOTICE,
  NO_FOLLOW_UPS_DESCRIPTION,
  NO_FOLLOW_UPS_TITLE,
  OPENERS_HEADING,
  OPENERS_SUBHEAD,
  OTHER_OPENERS_LABEL,
  REDUCED_SUMMARY_DESCRIPTION,
  REDUCED_SUMMARY_TITLE,
  eventRecapLine,
  itemIsOutstanding,
  itemStateLabel,
  roleAndCompany,
} from './constants';
import { FollowUpComposerDialog } from './follow-up-composer-dialog';
import type { FollowUpDetail, FollowUpItem } from './types';

/**
 * **The Follow-up page** — `Web App.dc.html` §2.7, `showFollowup` + `fuDetail`, lines 568–599
 * (`MEM-035`…`MEM-038`, `MEM-053` AC-1, AC-7, `MEM-056` AC-2).
 *
 * ## Top to bottom, as the prototype draws it
 *
 * The back link to Your Events and the event header card with *After · Follow-up* active — the card
 * that replaced this screen's old event eyebrow — then **Ready-to-send openers** with its sentence,
 * then a **2-column** grid of opener cards (§3.12): the person, the editable draft in a textarea,
 * and an action. One column on a phone (`MEM-056` AC-2).
 *
 * ## Copy is still the primary action, and that is deliberate for now
 *
 * §3.12's button is **Send opener**, and Sumit has ruled that Send becomes primary with Copy
 * beside it (`rulings-2026-09-11.md`, answer 2; ADR 052). **Send needs a message surface**, which
 * is the plan's Phase 6 — so today the card's primary action is `MEM-038` AC-1's *Copy opener*, in
 * §3.12's own button, and `MEM-038`'s reworded AC-1 and AC-3 stay unapplied until `MEM-054` makes
 * them true. The card is built so Send slots in beside Copy without moving anything: the action row
 * is a flex row of pills and the draft above it is already the message that would be sent.
 *
 * ## What the prototype does not draw and the specification requires
 *
 *   - **The two counts** — Connections and To follow up — which `MEM-036` AC-3 puts *"side by
 *     side"*. They sit under the heading, in the prototype's own tile treatment.
 *   - **The channels** (`MEM-038` AC-2, AC-6): copying is only useful if the member can see where
 *     to paste it, and a connection with no published channel is told so plainly rather than
 *     offered a copy button into a void.
 *   - **Done** (`MEM-038` AC-4), reachable from the item, for somebody who handled it another way.
 *   - **The reduced summary** (`MEM-035` AC-4, AC-5), which replaces the grid for a member who
 *     logged nobody. `reduced` is a fact on the payload rather than `items.length === 0`: an empty
 *     grid is a table with no rows, and a recap is a deliberate absence of a table.
 *
 * ## And what it draws that is not built
 *
 * §3.12's **Priority** pill. The prototype's `priority` is a fixture flag on two of its five seed
 * contacts. The true signal is the member's own *I want to follow up* from the capture sheet
 * (`MEM-029` AC-3) — which lives on `member-in-event`'s capture record, not on a follow-up item, and
 * putting it there is a cross-domain read that no criterion asks for. Listed in the pull request.
 */
export function FollowUpDetailPage(): React.ReactElement {
  const { eventId = '' } = useParams<{ eventId: string }>();

  return (
    <PerEventShell eventId={eventId} surface="follow-up" locked={PER_EVENT_LOCKED.followUp}>
      {() => <FollowUpBody eventId={eventId} />}
    </PerEventShell>
  );
}

function FollowUpBody({ eventId }: { readonly eventId: string }): React.ReactElement {
  const query = useFollowUpForEvent(eventId, { enabled: eventId.length > 0 });

  if (query.isPending) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-2/3" />
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <Skeleton className="h-[260px] w-full rounded-xl" />
          <Skeleton className="h-[260px] w-full rounded-xl" />
        </div>
      </div>
    );
  }

  const followUp = query.data?.followUp ?? null;
  if (followUp === null) {
    /* §2.7, item 4's empty card — for an event whose summary does not exist, which is every
       upcoming event reached through the After phase, and every member the door never scanned.

       **Not the prototype's `inbox` glyph.** The guard that used to refuse a mail tray anywhere in
       this slice was retired with ADR 032, and the glyph is still wrong for a reason that outlived
       it: a follow-up is a draft somebody writes rather than mail that arrives, and `/app/messages`
       is where mail arrives now (`MEM-055`). A pen is what this screen is short of. */
    return (
      <EmptyState
        icon={<PenLine className="h-6 w-6" aria-hidden />}
        title={NO_FOLLOW_UPS_TITLE}
        description={NO_FOLLOW_UPS_DESCRIPTION}
        action={<BackToYourEvents />}
      />
    );
  }

  return (
    <div className="space-y-7">
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div className="min-w-0">
          <h2 className="mb-1.5 text-2xl tracking-tight md:text-3xl">{OPENERS_HEADING}</h2>
          <p className="max-w-[600px] text-md text-muted-foreground md:text-lg">
            {OPENERS_SUBHEAD}
          </p>
        </div>
        <SummaryCounts followUp={followUp} />
      </div>

      {followUp.reduced ? (
        <EmptyState
          icon={<CalendarCheck className="h-6 w-6" aria-hidden />}
          title={REDUCED_SUMMARY_TITLE}
          description={REDUCED_SUMMARY_DESCRIPTION}
          // `MEM-035` AC-4's recap — name, date, venue — and nothing else. Not a call to action:
          // an empty state that ends in "find more people next time" is the reprimand AC-5 rules
          // out, so the last thing on this screen is a statement of where the member was.
          action={<p className="text-xs text-muted-foreground">{eventRecapLine(followUp.event)}</p>}
        />
      ) : (
        /* §2.7, item 5: `repeat(2, minmax(0,1fr)); gap:20px`. */
        <ul className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {followUp.items.map((item) => (
            <li key={item.attendeeRef} className="min-w-0">
              <OpenerCard eventId={eventId} item={item} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * `MEM-036` AC-3 — *"a summary card shows two counts side by side: Connections and the follow-up
 * count"*. The prototype's counts are hard-coded `2` and `2`; these are the member's own.
 */
function SummaryCounts({ followUp }: { readonly followUp: FollowUpDetail }): React.ReactElement {
  return (
    <dl className="flex flex-none gap-3" data-testid="follow-up-counts">
      {[
        { label: CONNECTIONS_COUNT_LABEL, value: followUp.connectionCount },
        { label: FOLLOW_UP_COUNT_LABEL, value: followUp.followUpCount },
      ].map((tile) => (
        <div
          key={tile.label}
          className="min-w-[104px] rounded-lg border border-border-subtle bg-card px-4 py-3 shadow-xs"
        >
          <dd className="font-brand text-2xl font-extrabold text-foreground">{tile.value}</dd>
          <dt className="mt-0.5 font-brand text-2xs font-semibold uppercase tracking-caps text-text-subtle">
            {tile.label}
          </dt>
        </div>
      ))}
    </dl>
  );
}

/**
 * One opener card — `Web App.dc.html` §3.12.
 *
 * The head is a 46px avatar, the name and the role. The body is the **editable draft** in a
 * textarea on `--surface-page-alt` with a 1.5px border, seeded from the stored draft and falling
 * back to the first generated opener. The foot is the action row.
 *
 * ## The draft is theirs (`MEM-037` AC-4)
 *
 * Saved on blur rather than on every keystroke, and **never merged with the openers** — the request
 * carries `draft` alone, so no template can be re-applied over something somebody wrote. Reopening
 * the page does not re-seed, because `item.draft` is what the API returns after a save.
 *
 * ## Three actions, and one more behind a link
 *
 * **Send** is the primary (`MEM-038` AC-1 under ADR 052): it delivers the draft as an ordinary
 * message on the pair's thread and the item moves to **sent** *because a message was sent*.
 * **Copy opener** stays as a secondary action — Sumit, 2026-09-11, *"ok"*, an addition to the
 * specification, confirmed — and it still moves the item to `sent` on the member's own assertion.
 * Both stop the nudge. *Done* clears the row without either (AC-4). *Other openers* opens the
 * composer, which is `MEM-037` AC-2 and AC-3's overlay.
 *
 * **Copy is not a lesser Send, and that is why it stayed.** A connection the member has since
 * removed refuses a send (`MEM-054` AC-5) and Copy is then the only route to that person; so is a
 * connection who would rather hear from them on LinkedIn, which is what the channels beside it
 * are for. The refusal is rendered on the card rather than thrown away, next to the control that
 * still works.
 */
function OpenerCard({
  eventId,
  item,
}: {
  readonly eventId: string;
  readonly item: FollowUpItem;
}): React.ReactElement {
  const copy = useCopyFollowUp();
  const send = useSendFollowUp();
  const markDone = useMarkFollowUpDone();
  const saveDraft = useSaveFollowUpDraft();
  const showError = useErrorToast();

  const seeded = item.draft ?? item.openers[0] ?? '';
  const [draft, setDraft] = React.useState(seeded);
  const [copied, setCopied] = React.useState(false);
  const [composing, setComposing] = React.useState(false);
  const [refusal, setRefusal] = React.useState<string | null>(null);

  const name = [item.firstName, item.lastName].filter(Boolean).join(' ');
  const subtitle = roleAndCompany(item);
  const outstanding = itemIsOutstanding(item.state);

  /**
   * `MEM-038` AC-3 — the clipboard first, the state second.
   *
   * The state change follows a successful write to the clipboard rather than accompanying the
   * click, because *sent* is the member's assertion about something that happened. If the clipboard
   * refuses — an insecure context, a permission — the item stays `ready` and the member still has
   * the text in front of them to select by hand, which is what the toast says.
   */
  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(draft);
    } catch (error) {
      showError(error, COPY_FAILED);
      return;
    }
    setCopied(true);
    if (draft !== item.draft) {
      await saveDraft.mutateAsync({ eventId, attendeeRef: item.attendeeRef, draft });
    }
    await copy.mutateAsync({ eventId, attendeeRef: item.attendeeRef });
  };

  /**
   * `MEM-038` AC-1 — the message goes, and only then does the item move.
   *
   * The route does both in that order (`follow-up/routes.ts`), so this hook's success *is* the
   * item having moved. A refusal is rendered beside the buttons rather than toasted: the draft is
   * still in front of the member, Copy still works, and the sentence has to be where they will
   * look next.
   */
  const onSend = (): void => {
    setRefusal(null);
    send.mutate(
      { eventId, attendeeRef: item.attendeeRef, body: draft },
      {
        onError: (error) => {
          setRefusal(sendRefusalSentence(error));
        },
      },
    );
  };

  return (
    <article className="flex h-full flex-col rounded-xl border border-border-subtle bg-card p-5 shadow-sm">
      <div className="mb-3.5 flex items-center gap-3">
        <span
          className="inline-flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-pill bg-brand-100 font-brand text-[15px] font-bold text-brand-700"
          aria-hidden
        >
          {initialsOf(item)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-brand text-md font-bold text-foreground">{name}</p>
          {subtitle === null ? null : (
            <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
          )}
        </div>
        {/* AC-4 / `MEM-038` AC-5: `sent` and `done` stay distinguishable — the label differs, and
            the badge tone separates "followed up" from "handled". */}
        {outstanding ? null : (
          <Badge variant={item.state === 'sent' ? 'success' : 'muted'}>
            <Check className="mr-1 h-3 w-3" aria-hidden />
            {itemStateLabel(item.state)}
          </Badge>
        )}
      </div>

      <label className="sr-only" htmlFor={`opener-${item.attendeeRef}`}>
        {`${DRAFT_LABEL} — ${name}`}
      </label>
      <Textarea
        id={`opener-${item.attendeeRef}`}
        value={draft}
        rows={4}
        className="mb-3.5 min-h-[104px] resize-none bg-muted/40"
        onChange={(changed) => {
          setDraft(changed.target.value);
          setCopied(false);
        }}
        onBlur={() => {
          if (draft !== (item.draft ?? '') && draft.trim().length > 0) {
            saveDraft.mutate({ eventId, attendeeRef: item.attendeeRef, draft });
          }
        }}
      />

      {/* `MEM-038` AC-2 and AC-6: where to paste it, or the sentence saying there is nowhere. */}
      <div className="mb-3.5">
        <p className="mb-1.5 font-brand text-2xs font-semibold uppercase tracking-caps text-text-subtle">
          {CHANNELS_LABEL}
        </p>
        {item.channels.length === 0 ? (
          <p className="text-xs text-muted-foreground">{NO_CHANNEL_NOTICE}</p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {item.channels.map((channel) => (
              <li key={channel.url}>
                <a
                  href={channel.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1.5 rounded-pill border border-border px-3 py-1.5 text-xs"
                >
                  {channel.label}
                  <ExternalLink className="h-3 w-3" aria-hidden />
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>

      {copied ? (
        <p role="status" className="mb-2 text-xs text-success">
          {COPIED_NOTICE}
        </p>
      ) : null}
      {refusal === null ? null : (
        <p role="alert" className="mb-2 text-xs text-destructive">
          {refusal}
        </p>
      )}

      {/* §3.12's foot, with Send in the prototype's own primary slot and Copy beside it as the
          secondary action Sumit confirmed on 2026-09-11. */}
      <div className="mt-auto flex flex-wrap items-center gap-2.5">
        <button
          type="button"
          disabled={draft.trim().length === 0 || send.isPending || !outstanding}
          aria-label={sendOpenerAccessibleName(name)}
          onClick={onSend}
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-pill bg-spark p-2.5 font-brand text-sm font-bold text-white disabled:opacity-60"
        >
          <Send className="h-[15px] w-[15px]" aria-hidden />
          {SEND_OPENER_LABEL}
        </button>
        <button
          type="button"
          disabled={draft.trim().length === 0 || copy.isPending}
          onClick={() => void onCopy()}
          className="inline-flex flex-none items-center justify-center gap-1.5 rounded-pill border-[1.5px] border-border bg-card px-4 py-2.5 font-brand text-sm font-bold text-text-body disabled:opacity-60"
        >
          <Copy className="h-[15px] w-[15px]" aria-hidden />
          {copied ? COPIED_LABEL : COPY_OPENER_LABEL}
        </button>
        {outstanding ? (
          <button
            type="button"
            title={MARK_DONE_DESCRIPTION}
            aria-label={`${MARK_DONE_LABEL} — ${MARK_DONE_DESCRIPTION}`}
            disabled={markDone.isPending}
            onClick={() => {
              markDone.mutate({ eventId, attendeeRef: item.attendeeRef });
            }}
            className="inline-flex flex-none items-center justify-center rounded-pill border-[1.5px] border-border bg-card px-4 py-2.5 font-brand text-sm font-bold text-muted-foreground disabled:opacity-60"
          >
            {MARK_DONE_LABEL}
          </button>
        ) : null}
        {item.openers.length === 0 ? null : (
          <button
            type="button"
            onClick={() => {
              setComposing(true);
            }}
            className="font-brand text-sm font-bold text-primary"
          >
            {OTHER_OPENERS_LABEL}
          </button>
        )}
      </div>

      {composing ? (
        <FollowUpComposerDialog
          eventId={eventId}
          item={item}
          initialDraft={draft}
          onDraftChange={setDraft}
          onClose={() => {
            setComposing(false);
          }}
        />
      ) : null}
    </article>
  );
}

/**
 * The server's refusal, in the member's words (`MEM-054` AC-5).
 *
 * 409 is the relationship having changed since the summary was generated — the commonest of the
 * two by far, a member who disconnected from somebody and then opened their follow-up list. 429
 * is the day's new-thread limit. Anything else is *"that did not send"*, which is the honest
 * thing to say about a state the member cannot act on.
 */
function sendRefusalSentence(error: Error): string {
  if (!(error instanceof ApiRequestError)) return SEND_OPENER_FAILED;
  if (error.statusCode === 429) return SEND_OPENER_RATE_LIMITED;
  if (error.statusCode === 409) return SEND_OPENER_DISCONNECTED;
  return SEND_OPENER_FAILED;
}
